import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AgentLayout from './AgentLayout';
import axios from 'axios';
import { useAgentAuth } from '../../context/AgentAuthContext';
import {
  Wallet,
  Plane,
  TrendingUp,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Users,
  CreditCard,
  LifeBuoy,
} from 'lucide-react';
import { useAgentSupportBadge } from '../../hooks/useAgentSupportBadge';
import { getAgentPaymentNavState } from '../../utils/agentPaymentNav';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getApiBookingRouteId, getUserFacingBookingId } from '../../utils/userFacingBookingId';
import BookingStatusChips from '../../components/booking/BookingStatusChips';

const AgentDashboard = () => {
  const { agent } = useAgentAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTxn, setExpandedTxn] = useState(null); // For expandable transaction details
  const [creditSummary, setCreditSummary] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const { isCreditAgent, walletEnabled } = getAgentPaymentNavState(agent);
  const { needReplyCount } = useAgentSupportBadge(Boolean(agent));

  const fetchDashboardData = useCallback(async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/api/agent/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/agent/dashboard/bookings-recent?limit=5`, { withCredentials: true }),
        axios.get(`${API_URL}/api/agent/dashboard/revenue-chart?period=month`, { withCredentials: true }),
      ];
      if (walletEnabled) {
        requests.push(
          axios.get(`${API_URL}/api/agent/wallet/transactions?page=1&limit=5`, { withCredentials: true }),
        );
      }
      if (isCreditAgent) {
        requests.push(
          axios.get(`${API_URL}/api/agent/credit/balance`, { withCredentials: true }),
        );
      }

      const results = await Promise.all(requests);
      let idx = 0;
      setStats(results[idx++].data);
      setRecentBookings(results[idx++].data.bookings);
      setChartData(results[idx++].data.data);
      if (walletEnabled) {
        setRecentTransactions(results[idx++].data.transactions);
      } else {
        setRecentTransactions([]);
      }
      if (isCreditAgent) {
        setCreditSummary(results[idx++].data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, walletEnabled, isCreditAgent]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AgentLayout>
    );
  }

  const statCards = [
    ...(walletEnabled ? [{
      title: 'Wallet Balance',
      value: `₹${stats?.wallet_balance?.toLocaleString() || 0}`,
      icon: Wallet,
      color: 'from-teal-500 to-cyan-600',
      textColor: 'text-teal-600',
      bgColor: 'bg-teal-50',
      change: null,
    }] : []),
    ...(isCreditAgent ? [{
      title: 'Credit Available',
      value: `₹${Number(creditSummary?.credit_available ?? agent?.credit_available ?? 0).toLocaleString('en-IN')}`,
      icon: CreditCard,
      color: 'from-[#1B375B] to-[#2d4d7a]',
      textColor: 'text-[#1B375B]',
      bgColor: 'bg-slate-50',
      change: creditSummary?.credit_status
        ? `Status: ${creditSummary.credit_status}`
        : 'Credit line',
    }] : []),
    {
      title: 'Total Bookings',
      value: stats?.total_bookings || 0,
      icon: Plane,
      color: 'from-blue-500 to-blue-600',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      change: `${stats?.month_bookings || 0} this month`
    },
    {
      title: 'Total Revenue',
      value: `₹${stats?.total_revenue?.toLocaleString() || 0}`,
      icon: TrendingUp,
      color: 'from-green-500 to-emerald-600',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50',
      change: `₹${stats?.month_revenue?.toLocaleString() || 0} this month`
    },
    ...(walletEnabled ? [{
      title: 'Pending Requests',
      value: stats?.pending_topup_requests || 0,
      icon: AlertCircle,
      color: 'from-orange-500 to-amber-600',
      textColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      change: 'Wallet topup requests',
    }] : []),
  ];

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Dashboard Overview</h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's your business summary.</p>
        </div>

        {needReplyCount > 0 && (
          <Link
            to="/agent/support-tickets?status=need_reply"
            className="block bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 hover:border-amber-300 transition-colors"
            data-testid="agent-dashboard-support-widget"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <LifeBuoy className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">Support tickets need your reply</p>
                  <p className="text-sm text-amber-800/90">
                    {needReplyCount} ticket{needReplyCount !== 1 ? 's' : ''} waiting for your response
                  </p>
                </div>
              </div>
              <span className="text-sm font-semibold text-amber-800">View →</span>
            </div>
          </Link>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
                    <Icon className="text-white" size={24} />
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                <p className={`text-3xl font-bold ${stat.textColor}`}>{stat.value}</p>
                {stat.change && (
                  <p className="text-xs text-gray-500 mt-2">{stat.change}</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Revenue Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Monthly Revenue</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="_id" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="revenue" fill="url(#colorRevenue)" radius={[8, 8, 0, 0]} />
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.9}/>
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.7}/>
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent Bookings & Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Bookings */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Recent Bookings</h2>
              <a href="/agent/bookings" className="text-sm text-blue-600 hover:underline">
                View All
              </a>
            </div>
            <div className="space-y-3">
              {recentBookings.length > 0 ? (
                recentBookings.map((booking, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/booking-details/${getApiBookingRouteId(booking) || booking.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-800 truncate">
                          {booking.route || 'N/A'}
                        </p>
                        {booking.booking_type && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                            {booking.booking_type}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {booking.passenger_name || 'Passenger'} • PNR: {booking.pnr || 'Pending'}
                      </p>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">{getUserFacingBookingId(booking)}</p>
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <p className="font-semibold text-gray-800">₹{booking.total_amount?.toLocaleString() || '0'}</p>
                      <BookingStatusChips booking={booking} className="items-end" />
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No bookings yet</p>
              )}
            </div>
          </div>

          {walletEnabled ? (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Recent Wallet Transactions</h2>
              <a href="/agent/wallet" className="text-sm text-blue-600 hover:underline">
                View All
              </a>
            </div>
            <div className="space-y-3">
              {recentTransactions.length > 0 ? (
                recentTransactions.map((txn, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Main Transaction Row */}
                    <div 
                      className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => setExpandedTxn(expandedTxn === index ? null : index)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {txn.type === 'credit' ? (
                          <ArrowUpRight className="text-green-500 flex-shrink-0" size={20} />
                        ) : (
                          <ArrowDownRight className="text-red-500 flex-shrink-0" size={20} />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 truncate">
                            {txn.transaction_id || txn.id || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-600 capitalize">
                            {txn.description || txn.method || 'Transaction'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`font-semibold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {txn.type === 'credit' ? '+' : '-'}₹{txn.amount?.toLocaleString()}
                        </p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          txn.status === 'completed' ? 'bg-green-100 text-green-700' :
                          txn.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {txn.status}
                        </span>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedTxn === index && txn.booking_details && (
                      <div className="px-4 py-3 bg-white border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 mb-2">BOOKING DETAILS</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {txn.booking_details.route && (
                            <div>
                              <p className="text-gray-500 text-xs">Route</p>
                              <p className="font-medium text-gray-800">{txn.booking_details.route}</p>
                            </div>
                          )}
                          {txn.booking_details.passenger_name && (
                            <div>
                              <p className="text-gray-500 text-xs">Passenger</p>
                              <p className="font-medium text-gray-800">{txn.booking_details.passenger_name}</p>
                            </div>
                          )}
                          {txn.booking_id && (
                            <div>
                              <p className="text-gray-500 text-xs">Booking ID</p>
                              <p className="font-medium text-gray-800 text-xs">{getUserFacingBookingId({ booking_id: txn.booking_id, ...txn.booking_details })}</p>
                            </div>
                          )}
                          {txn.booking_details.booking_type && (
                            <div>
                              <p className="text-gray-500 text-xs">Type</p>
                              <p className="font-medium text-gray-800 capitalize">{txn.booking_details.booking_type}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No transactions yet</p>
              )}
            </div>
          </div>
          ) : isCreditAgent ? (
          <div
            className="bg-gradient-to-br from-[#1B375B] to-[#2d4d7a] text-white rounded-xl shadow-sm p-6"
            data-testid="dashboard-credit-summary"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Credit Account</h2>
              <a href="/agent/credit" className="text-sm text-teal-200 hover:text-white underline">
                Manage
              </a>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-white/70">Available</p>
                <p className="text-2xl font-bold">
                  ₹{Number(creditSummary?.credit_available ?? 0).toLocaleString('en-IN')}
                </p>
              </div>
              <div>
                <p className="text-xs text-white/70">Utilized</p>
                <p className="text-2xl font-bold text-amber-200">
                  ₹{Number(creditSummary?.credit_used ?? 0).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
            {Number(creditSummary?.overdue_amount || 0) > 0 && (
              <p className="text-sm text-red-200 mb-3">
                Overdue: ₹{Number(creditSummary.overdue_amount).toLocaleString('en-IN')}
              </p>
            )}
            <a
              href="/agent/credit"
              className="inline-flex items-center gap-2 text-sm font-semibold bg-white text-[#1B375B] px-4 py-2 rounded-lg hover:bg-white/90"
            >
              <CreditCard size={18} />
              View billing &amp; transactions
            </a>
          </div>
          ) : null}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {walletEnabled && (
          <a
            href="/agent/wallet"
            className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:scale-105"
            data-testid="dashboard-wallet-topup"
          >
            <Wallet size={32} className="mb-3" />
            <h3 className="text-lg font-semibold">Request Topup</h3>
            <p className="text-sm text-teal-50 mt-1">Add funds to your wallet</p>
          </a>
          )}
          {isCreditAgent && (
          <a
            href="/agent/credit"
            className="bg-gradient-to-br from-[#1B375B] to-[#2d4d7a] text-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:scale-105"
            data-testid="dashboard-credit-account"
          >
            <CreditCard size={32} className="mb-3" />
            <h3 className="text-lg font-semibold">My Credit</h3>
            <p className="text-sm text-white/80 mt-1">Billing cycles &amp; settlements</p>
          </a>
          )}
          <a
            href="/agent/bookings"
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:scale-105"
          >
            <Plane size={32} className="mb-3" />
            <h3 className="text-lg font-semibold">My Bookings</h3>
            <p className="text-sm text-blue-50 mt-1">View booking history</p>
          </a>
          <a
            href="/agent/travellers"
            className="bg-gradient-to-br from-green-500 to-emerald-600 text-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all transform hover:scale-105"
          >
            <Users size={32} className="mb-3" />
            <h3 className="text-lg font-semibold">My Travellers</h3>
            <p className="text-sm text-green-50 mt-1">Manage saved passengers</p>
          </a>
        </div>
      </div>
    </AgentLayout>
  );
};

export default AgentDashboard;
